/**
 * Multi-polygon area tool with persistent measurements.
 *
 *   Left-click  → drop a polygon vertex
 *   Right-click → close + register the polygon (stays visible).
 *                 Next left-click starts a fresh polygon.
 *
 * Each finished polygon is its own Three.js Group so the Ebenen-Panel
 * can toggle visibility independently.  Outline + cone markers use the
 * same look as DistanceTool.
 */

import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  Scene as ThreeScene,
  Sprite,
  Vector3,
} from 'three'
import { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'

import { buildMarkerCone } from './markerCone'
import {
  disposeChildren,
  makePixelLabel,
  type MeasurementListener,
} from './DistanceTool'
import { Measurement, nextId } from '../measurements'

const COLOR_OUTLINE = 0x000000
// World-space line thickness in metres — verbatim from 3DRoad markers.js.
const LINEWIDTH_M = 0.009
const OUTLINE_WIDTH_M = 0.012
/** Base marker radius — same as 3DRoad core/loader-wrapper.js. */
const MARKER_RADIUS = 0.09

/**
 * Returns the topmost surface point under (x, y) — used by the Fläche
 * label so it sits above any kerb / step inside the polygon, not just
 * above the polygon's vertices.  null = no surface hit.
 */
export type SurfaceTopProbe = (x: number, y: number) => Vector3 | null

export class AreaTool {
  private current: { vertices: Vector3[]; group: Object3D; outline: Object3D } | null = null
  private color: number
  private listener: MeasurementListener | null = null
  private serial = 0
  private surfaceTopProbe: SurfaceTopProbe | null = null
  /** Multiplier on LINEWIDTH_M / OUTLINE_WIDTH_M (live-adjustable). */
  private lineScale = 1
  /** Multiplier on label base size (live-adjustable). */
  private labelScale = 1

  constructor(private scene: ThreeScene, color: number = 0x4dd2ff) {
    this.color = color
  }

  setColor(color: number): void {
    this.color = color
  }

  /** Scale every measurement-label sprite (existing + new). */
  setLabelScale(scale: number): void {
    this.labelScale = scale
    this.scene.traverse((child) => {
      const ud = (child as Sprite).userData as
        | { baseScaleX?: number; baseScaleY?: number; isMeasureLabel?: boolean }
        | undefined
      if (
        ud?.isMeasureLabel &&
        typeof ud.baseScaleX === 'number' &&
        typeof ud.baseScaleY === 'number'
      ) {
        ;(child as Sprite).scale.set(ud.baseScaleX * scale, ud.baseScaleY * scale, 1)
      }
    })
  }

  /** Scale all line thicknesses (existing + new). 1.0 = stock 3DRoad. */
  setLineScale(scale: number): void {
    this.lineScale = scale
    this.scene.traverse((child) => {
      const m = (child as Line2).material as LineMaterial | undefined
      if (m && (m as LineMaterial).isLineMaterial) {
        const base = (m as unknown as { userData: { baseLinewidth?: number } }).userData?.baseLinewidth
        if (typeof base === 'number') m.linewidth = base * scale
      }
    })
  }

  setListener(fn: MeasurementListener | null): void {
    this.listener = fn
  }

  /** Plug in a surface-Z probe (top-down raycast).  When supplied,
   * the Fläche label rides above the actual topmost terrain inside
   * the polygon, not just the polygon's flat vertex plane. */
  setSurfaceTopProbe(fn: SurfaceTopProbe | null): void {
    this.surfaceTopProbe = fn
  }

  setResolution(width: number, height: number): void {
    this.scene.traverse((child) => {
      const m = (child as Line2).material as LineMaterial | undefined
      if (m && m.resolution && (m as LineMaterial).isLineMaterial) {
        m.resolution.set(width, height)
      }
    })
  }

  handleClick(point: Vector3): void {
    if (!this.current) {
      const group = new Object3D()
      group.name = 'area-current'
      const outline = new Object3D()
      group.add(outline)
      this.scene.add(group)
      this.current = { vertices: [], group, outline }
    }
    this.current.vertices.push(point.clone())
    this.redrawOutline(false)
  }

  /** Right-click — close polygon + register. */
  close(): void {
    if (!this.current || this.current.vertices.length < 3) {
      if (this.current) {
        this.scene.remove(this.current.group)
        disposeChildren(this.current.group)
      }
      this.current = null
      return
    }
    const { vertices, group } = this.current
    const area = computeShoelace2D(vertices)
    this.drawFill(group, vertices)
    this.redrawOutline(true) // close visually too
    this.placeLabel(group, vertices, area)

    this.serial += 1
    const measurement: Measurement = {
      id: nextId(),
      kind: 'area',
      label: `Fläche ${this.serial} — ${area.toFixed(2)} m²`,
      visible: true,
      group,
      color: this.color,
    }
    group.name = `area-${measurement.id}`
    group.userData.measurementId = measurement.id
    this.current = null
    this.listener?.({ added: measurement })
  }

  removeMeasurement(_id: string, group: Object3D): void {
    this.scene.remove(group)
    disposeChildren(group)
  }

  /** Bump the per-session serial so newly created polygons don't reuse
   * "Fläche 1" after restoring existing annotations. */
  setSerial(value: number): void {
    if (value > this.serial) this.serial = value
  }

  /** Build a closed polygon group from a saved payload (≥ 3 vertices). */
  buildGroupFromPayload(vertices: Vector3[], color: number): { group: Object3D; area: number } {
    if (vertices.length < 3) {
      throw new Error('area annotation needs ≥ 3 points')
    }
    const group = new Object3D()
    const outline = new Object3D()
    group.add(outline)
    this.scene.add(group)

    const previousColor = this.color
    this.color = color
    try {
      // Outline + vertex cones — same look as the live tool after close().
      for (const v of vertices) {
        outline.add(buildMarkerCone(v, color, MARKER_RADIUS))
      }
      const positions: number[] = []
      for (const v of vertices) positions.push(v.x, v.y, v.z)
      positions.push(vertices[0].x, vertices[0].y, vertices[0].z)
      outline.add(makeThickLine(positions, COLOR_OUTLINE, OUTLINE_WIDTH_M, 999, this.lineScale))
      outline.add(makeThickLine(positions, color, LINEWIDTH_M, 1000, this.lineScale))

      this.drawFill(group, vertices)
      const area = computeShoelace2D(vertices)
      this.placeLabel(group, vertices, area)
      return { group, area }
    } finally {
      this.color = previousColor
    }
  }

  clearActive(): void {
    if (!this.current) return
    this.scene.remove(this.current.group)
    disposeChildren(this.current.group)
    this.current = null
  }

  dispose(): void {
    this.clearActive()
  }

  // ── private ───────────────────────────────────────────────────────────

  private redrawOutline(closed: boolean): void {
    if (!this.current) return
    disposeChildren(this.current.outline)
    while (this.current.outline.children.length > 0) {
      this.current.outline.remove(this.current.outline.children[0])
    }

    for (const v of this.current.vertices) {
      this.current.outline.add(buildMarkerCone(v, this.color, MARKER_RADIUS))
    }
    if (this.current.vertices.length < 2) return

    const positions: number[] = []
    for (const v of this.current.vertices) positions.push(v.x, v.y, v.z)
    if (closed) {
      positions.push(this.current.vertices[0].x, this.current.vertices[0].y, this.current.vertices[0].z)
    }
    this.current.outline.add(makeThickLine(positions, COLOR_OUTLINE, OUTLINE_WIDTH_M, 999, this.lineScale))
    this.current.outline.add(makeThickLine(positions, this.color, LINEWIDTH_M, 1000, this.lineScale))
  }

  private drawFill(group: Object3D, vertices: Vector3[]): void {
    const n = vertices.length
    const positions = new Float32Array(n * 3)
    vertices.forEach((v, i) => {
      positions[i * 3 + 0] = v.x
      positions[i * 3 + 1] = v.y
      positions[i * 3 + 2] = v.z
    })
    const indices: number[] = []
    for (let i = 1; i < n - 1; i++) indices.push(0, i, i + 1)
    const geom = new BufferGeometry()
    geom.setAttribute('position', new BufferAttribute(positions, 3))
    geom.setIndex(indices)
    const mat = new MeshBasicMaterial({
      color: this.color,
      transparent: true,
      opacity: 0.25,
      depthTest: false,  // always draw over the model (kerbs / steps inside polygon)
      depthWrite: false,
      side: DoubleSide,
    })
    const fill = new Mesh(geom, mat)
    fill.renderOrder = 998
    group.add(fill)
  }

  private placeLabel(group: Object3D, vertices: Vector3[], area: number): void {
    const centroid = computeCentroid(vertices)

    // Find the *topmost* terrain Z across a sample of points inside the
    // polygon — not just the polygon's vertex Z.  Kerbs / steps inside
    // the area would otherwise occlude the label when the camera dips
    // low.  Falls back to vertex max if no surface probe is wired.
    let topZ = -Infinity
    for (const v of vertices) if (v.z > topZ) topZ = v.z

    if (this.surfaceTopProbe) {
      for (const v of vertices) {
        const hit = this.surfaceTopProbe(v.x, v.y)
        if (hit && hit.z > topZ) topZ = hit.z
      }
      // also sample midpoints of edges + centroid for steps that sit
      // between vertices
      for (let i = 0; i < vertices.length; i++) {
        const a = vertices[i]
        const b = vertices[(i + 1) % vertices.length]
        for (let t = 0.25; t < 1; t += 0.25) {
          const sx = a.x + (b.x - a.x) * t
          const sy = a.y + (b.y - a.y) * t
          const hit = this.surfaceTopProbe(sx, sy)
          if (hit && hit.z > topZ) topZ = hit.z
        }
      }
      const cHit = this.surfaceTopProbe(centroid.x, centroid.y)
      if (cHit && cHit.z > topZ) topZ = cHit.z
    }

    centroid.z = topZ + MARKER_RADIUS * 2
    const sprite = makePixelLabel(`${area.toFixed(2)} m²`, centroid, this.color, 0x000000, true, 16)
    sprite.userData.isMeasureLabel = true
    sprite.scale.multiplyScalar(this.labelScale)
    group.add(sprite)
  }
}

// ─── helpers ─────────────────────────────────────────────────────────────────

function computeShoelace2D(verts: Vector3[]): number {
  let s = 0
  const n = verts.length
  for (let i = 0, j = n - 1; i < n; j = i++) {
    s += (verts[j].x + verts[i].x) * (verts[i].y - verts[j].y)
  }
  return Math.abs(s) / 2
}

function computeCentroid(verts: Vector3[]): Vector3 {
  const c = new Vector3()
  verts.forEach((v) => c.add(v))
  c.multiplyScalar(1 / verts.length)
  return c
}

function makeThickLine(
  positions: number[],
  color: number,
  linewidth: number,
  renderOrder: number,
  scale: number = 1,
): Line2 {
  const geom = new LineGeometry()
  geom.setPositions(positions)
  const mat = new LineMaterial({
    color,
    linewidth: linewidth * scale,
    worldUnits: true,
    depthTest: false,
    transparent: true,
  })
  mat.userData.baseLinewidth = linewidth
  mat.resolution.set(window.innerWidth, window.innerHeight)
  const line = new Line2(geom, mat)
  line.computeLineDistances()
  line.renderOrder = renderOrder
  return line
}
